import React, { useCallback } from "react";
import { useMediaQuery } from "../../../hooks/common";

// Define PaginationComponentProps if it's not imported from react-data-table-component
interface PaginationComponentProps {
  rowsPerPage: number;
  rowCount: number;
  currentPage: number;
  onChangePage: (page: number, totalRows: number) => void;
}

function getNumberOfPages(rowCount: number, rowsPerPage: number): number {
  return Math.ceil(rowCount / rowsPerPage);
}

export default function Paginator({
  limit,
  page,
  count,
  setPage,
}: {
  page: number;
  limit: number;
  count?: number;
  setPage: (_: number) => any;
}) {
  const lastPossiblePage = Math.ceil((count || 0) / limit);
  const currentStart = count ? (page - 1) * limit + 1 : 0;
  const currentEnd = Math.min(currentStart - 1 + limit, count || 0);

  const renderPageButtons = () => {
    const buttons = [];
    const maxButtons = 3;
    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(lastPossiblePage, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setPage(i)}
          className={`px-2 py-1 mx-0.5 text-sm rounded ${
            i === page ? "bg-[#E9B925BF]" : "bg-white"
          }`}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center bg-white px-2 py-2 gap-2">
      <p className="text-sm text-center sm:text-left mb-2 sm:mb-0">
        {`Showing ${currentStart} to ${currentEnd} of ${count} entries`}
      </p>
      <div className="flex items-center space-x-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-2 py-1 text-sm rounded bg-white disabled:opacity-50 font-bold"
        >
          Previous
        </button>
        {renderPageButtons()}
        {lastPossiblePage > 4 && page < lastPossiblePage - 1 && (
          <span className="px-1">...</span>
        )}
        <button
          onClick={() => setPage(Math.min(lastPossiblePage, page + 1))}
          disabled={page === lastPossiblePage}
          className="px-2 py-1 text-sm rounded bg-white disabled:opacity-50 font-bold"
        >
          Next
        </button>
      </div>
    </div>
  );

}

export function PaginatorForRDT(
  props: PaginationComponentProps & {
    paginationIconLastPage?: React.ReactNode;
    paginationIconFirstPage?: React.ReactNode;
    paginationIconNext?: React.ReactNode;
    paginationIconPrevious?: React.ReactNode;
  }
) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  const {
    rowsPerPage,
    rowCount,
    currentPage,
    onChangePage = () => null,
  } = props;

  const numPages = getNumberOfPages(rowCount, rowsPerPage);
  const lastIndex = currentPage * rowsPerPage;
  const firstIndex = lastIndex - rowsPerPage + 1;
  const disabledLesser = currentPage === 1;
  const disabledGreater = currentPage === numPages;

  const range =
    currentPage === numPages
      ? `Showing ${firstIndex} to ${rowCount} of ${rowCount} entries`
      : `Showing ${firstIndex} to ${lastIndex} of ${rowCount} entries`;

  const handlePrevious = useCallback(
    () => onChangePage(currentPage - 1, rowCount),
    [currentPage, onChangePage, rowCount]
  );
  const handleNext = useCallback(
    () => onChangePage(currentPage + 1, rowCount),
    [currentPage, onChangePage, rowCount]
  );

  const renderPageButtons = () => {
    const buttons = [];
    const maxButtons = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(numPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => onChangePage(i, rowCount)}
          className={`px-2 py-1 mx-0.5 text-sm rounded ${
            i === currentPage ? "bg-[#E9B925BF]" : "bg-white"
          }`}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center bg-white px-2 py-2 gap-2">
      <p className="text-sm text-center sm:text-left mb-2 sm:mb-0">{range}</p>
      <div className="flex items-center space-x-1">
        <button
          onClick={handlePrevious}
          disabled={disabledLesser}
          className="px-2 py-1 text-sm rounded bg-white disabled:opacity-50 font-bold"
        >
          Previous
        </button>
        {renderPageButtons()}
        {numPages > (isMobile ? 3 : 5) && currentPage < numPages - 1 && (
          <span className="px-1">...</span>
        )}
        <button
          onClick={handleNext}
          disabled={disabledGreater}
          className="px-2 py-1 text-sm rounded bg-white disabled:opacity-50 font-bold"
        >
          Next
        </button>
      </div>
    </div>
  );
}
